import React, { useState, useEffect, useRef } from 'react';
import { Globe, MapPin, Satellite, Upload, ArrowRight, ArrowDown, X, Activity } from 'lucide-react';
import Card from '../components/ui/Card';
import SatelliteImage from '../components/ui/SatelliteImage';
import ApiService from '../services/apiService';
import { openImageInNewTab } from '../utils/helpers';
import { commitModule } from '../utils/reportMarkers';

import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Polygon, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const SatelliteView = ({ t, userId, isActive, setReportData }) => {
    const [analysisMode, setAnalysisMode] = useState('indices'); // 'indices' or 'count'
    const [aerialItems, setAerialItems] = useState([]); // For aerial images
    const [params, setParams] = useState({
        lat: 30.0444,
        lon: 31.2357,
        start_date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        radius_km: 1.0,
        max_cloud_cover: 10,
        compare_value: 3,
        compare_unit: "months",
        polygon_coords: [] // Store [lat, lon] arrays
    });

    const [isDrawing, setIsDrawing] = useState(false);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const resultsRef = useRef(null);
    const [locationMsg, setLocationMsg] = useState('');

    // دالة ذكية تنزل الشاشة أوتوماتيك أول ما النتائج تظهر
    useEffect(() => {
        if (results && resultsRef.current) {
            setTimeout(() => {
                resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 150); // تأخير بسيط عشان نضمن إن الصور اترسمت في الشاشة
        }
    }, [results]);
    // --- Map Handler for Clicks & Drawing ---
    const MapHandler = () => {
        const map = useMapEvents({
            click(e) {
                if (isDrawing) {
                    // Add point to polygon
                    if (params.polygon_coords.length < 5) {
                        setParams(prev => ({
                            ...prev,
                            polygon_coords: [...prev.polygon_coords, [e.latlng.lat, e.latlng.lng]]
                        }));
                    } else {
                        alert("Maximum 5 points allowed for this demo.");
                    }
                } else {
                    // Standard Point Selection
                    setParams(prev => ({
                        ...prev,
                        lat: parseFloat(e.latlng.lat.toFixed(4)),
                        lon: parseFloat(e.latlng.lng.toFixed(4)),
                        polygon_coords: [] // Clear polygon if picking point
                    }));
                }
            },
        });

        // Resize fix
        useEffect(() => {
            if (isActive) setTimeout(() => map.invalidateSize(), 100);
        }, [isActive, map]);

        return null;
    };

    const runAnalysis = async () => {
        if (isDrawing) {
            if (params.polygon_coords.length < 3) {
                alert("Please select at least 3 points for the polygon.");
                return;
            }

            // --- دالة لحساب مساحة الـ Polygon الدقيقة بالكيلومتر المربع ---
            const getPolygonAreaKm2 = (coords) => {
                let area = 0;
                const R = 6371; // نصف قطر الأرض بالكيلومتر
                for (let i = 0; i < coords.length; i++) {
                    let j = (i + 1) % coords.length;
                    let lon1 = coords[i][1] * (Math.PI / 180);
                    let lat1 = coords[i][0] * (Math.PI / 180);
                    let lon2 = coords[j][1] * (Math.PI / 180);
                    let lat2 = coords[j][0] * (Math.PI / 180);
                    area += (lon2 - lon1) * (Math.sin(lat1) + Math.sin(lat2)) / 2;
                }
                return Math.abs(area * R * R);
            };

            const areaKm2 = getPolygonAreaKm2(params.polygon_coords);
            if (areaKm2 < 1.0) {
                alert("صوره القمر اقل مساحه لها للصوره الواحده الواضحه لازم تكون 1 كيلو، كبر مساحه الشكل الي مختاره");
                return; // إيقاف العملية وعدم استهلاك رصيد
            }
        }

        setLoading(true);
        setResults(null);

        const targetDate = new Date(params.end_date);
        const chunkSizeDays = 35; // هنبحث كل أسبوعين (15 يوم) عشان الباك اند يكون سريع جداً
        const maxAttempts = 3;    // 6 محاولات × 15 يوم = 90 يوم (3 شهور)

        let isSuccess = false;

        // --- حلقة البحث المتدرج ---
        for (let i = 0; i < maxAttempts; i++) {
            // حساب نافذة البحث الحالية (15 يوم فقط)
            const chunkEnd = new Date(targetDate);
            chunkEnd.setDate(chunkEnd.getDate() - (i * chunkSizeDays));

            const chunkStart = new Date(chunkEnd);
            chunkStart.setDate(chunkStart.getDate() - chunkSizeDays);

            const strStart = chunkStart.toISOString().split('T')[0];
            const strEnd = chunkEnd.toISOString().split('T')[0];

            console.log(`Searching for satellite imagery from ${strStart} to ${strEnd}...`);

            try {
                const json = await ApiService.post('/satellite-analysis', {
                    ...params,
                    start_date: strStart,
                    end_date: strEnd,     // بنبعت بداية ونهاية النافذة الصغيرة
                    user_id: userId
                });

                // لو الباك اند لقى صورة واضحة ورجع نجاح
                if (json.status === "success") {
                    isSuccess = true;

                    // نخصم الرصيد مرة واحدة فقط لما نتأكد إننا لقينا داتا
                    window.dispatchEvent(new CustomEvent('credit-deducted', { detail: { amount: 5 } }));

                    setResults(json);
                    const cleanArea = Number(json.meta?.area_km2);

                    // Same four payload fields as before, plus the 'satellite' execution
                    // marker, written in a single state update (contract 1.3).
                    commitModule(setReportData, 'satellite', {
                        satellite_data: {
                            rgb: json.rgb,
                            ...(json.indices || {})
                        },
                        sat_date_current: json.meta?.date_current || "N/A",
                        sat_date_past: json.meta?.date_past || "N/A",
                        satellite_area: isNaN(cleanArea) ? 0 : cleanArea
                    }, true, null);

                    break; // 🟢 نخرج من اللوب فوراً ومندورش في التواريخ الأقدم
                }
                // لو status مش success (مفيش صورة واضحة)، اللوب هيكمل المحاولة اللي بعدها أوتوماتيك
            } catch (e) {
                console.error(`Attempt ${i + 1} failed (Likely no data or timeout):`, e);
                // بنكمل اللوب عادي عشان يجرب الـ 15 يوم اللي قبلهم
            }
        }

        if (!isSuccess) {
            // Ran, but no cloud-free scene was found. Marked ok:false so the report section
            // still renders with the reason instead of silently disappearing.
            commitModule(setReportData, 'satellite', {}, false,
                'No cloud-free satellite imagery found for this area in the last 3 months.');
            alert("لم يتم العثور على صور واضحة (خالية من السحب) لهذه المنطقة خلال الـ 3 شهور الماضية.");
        }

        setLoading(false);
    };
    const INDEX_ORDER = ['ndvi', 'ndmi', 'ndwi', 'ndre', 'gndvi', 'savi'];

    // Re-use helper classes
    const lblClass = "text-xs text-slate-400 block mb-1";
    const btnSecondaryClass = "flex items-center gap-1 bg-slate-700 px-3 py-1 rounded hover:bg-slate-600 transition text-white";
    const inputStyle = "w-full bg-slate-900/50 border border-slate-600 rounded p-2 text-white text-sm focus:border-emerald-500 outline-none";

    return (
        <div className="absolute inset-0 overflow-hidden font-sans text-[#1B263B] dark:text-white">

            {/* 🟢 FULL BLEED MAP BACKGROUND 🟢 */}
            <div className="absolute inset-0 z-0 bg-gray-100 dark:bg-slate-900">
                <MapContainer center={[29.5000, 34.5000]} zoom={6} style={{ height: '100%', width: '100%' }}>                    <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                    <MapHandler />
                    {params.polygon_coords.length > 0 && <Polygon positions={params.polygon_coords} color="#6B8F71" weight={4} fillOpacity={0.4} />}
                    {params.polygon_coords.length === 0 && params.lat && <Marker position={[params.lat, params.lon]} />}
                </MapContainer>


            </div>

            {/* 🟢 FLOATING MAP TOOLS (Top-Left) 🟢 */}
            <div className="absolute top-4 left-4 lg:left-8 z-[500] pointer-events-none flex flex-col gap-5">

                {/* Title / Mode Toggle */}
                <div className="pointer-events-auto flex flex-col gap-3">
                    <h2 className="text-3xl font-bold flex items-center gap-3 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                        <Globe className="text-[#6B8F71] flex-shrink-0" size={32} /> {t.satellite?.title || "Satellite Intelligence"}
                    </h2>
                    <div className="flex bg-white/90 dark:bg-slate-900/90 backdrop-blur-md rounded-full p-1 shadow-lg w-max border border-gray-200 dark:border-slate-700">
                        <button onClick={() => setAnalysisMode('indices')} className={`px-6 py-2 rounded-full font-medium transition-all ${analysisMode === 'indices' ? 'bg-[#1B263B] dark:bg-slate-700 text-white shadow-md' : 'text-gray-500 dark:text-gray-400 hover:text-[#1B263B] dark:hover:text-white'}`}>
                            {t.satellite?.modeIndices || "Soil & Crop Indices"}
                        </button>
                        <button onClick={() => setAnalysisMode('count')} className={`px-6 py-2 rounded-full font-medium transition-all ${analysisMode === 'count' ? 'bg-[#1B263B] dark:bg-slate-700 text-white shadow-md' : 'text-gray-500 dark:text-gray-400 hover:text-[#1B263B] dark:hover:text-white'}`}>
                            {t.satellite?.modeCount || "Aerial Palm Counting"}
                        </button>
                    </div>
                </div>

                {/* Draw Tools Map Buttons */}
                {analysisMode === 'indices' && (
                    <div className="pointer-events-auto">
                        <button
                            onClick={() => { setIsDrawing(!isDrawing); if (!isDrawing) setParams(p => ({ ...p, polygon_coords: [] })); }}
                            className={`px-4 py-3 rounded-xl font-bold shadow-lg text-sm border-2 transition-all flex items-center gap-2 ${isDrawing ? 'bg-[#6B8F71] text-white border-[#6B8F71]' : 'bg-white/90 dark:bg-slate-900/90 backdrop-blur-md text-[#1B263B] dark:text-white border-transparent hover:border-[#6B8F71] dark:hover:border-[#6B8F71]'}`}
                        >
                            {isDrawing ? "Finish Drawing" : "Draw Polygon"}
                        </button>
                    </div>
                )}
            </div>

            {/* 🟢 FLOATING RIGHT CONTROL PANEL 🟢 */}
            <div className="absolute top-4 right-4 bottom-4 w-full md:w-[500px] lg:w-[650px] bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl rounded-2xl shadow-2xl z-[500] border border-gray-200 dark:border-slate-700 flex flex-col overflow-hidden">

                {/* Sticky Header of Panel */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-800 bg-white/50 dark:bg-slate-800/50 backdrop-blur-md shrink-0 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#1B263B]/10 dark:bg-slate-800 flex items-center justify-center">
                        <Satellite className="text-[#C47A2C]" size={20} />
                    </div>
                    <div>
                        <h3 className="font-bold text-[#1B263B] dark:text-white text-lg leading-tight tracking-tight">Analysis Parameters</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Configure geometry and triggers</p>
                    </div>
                </div>

                {/* Scrollable Content inside Right Panel */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">

                    {/* --- INDICES MODE SETTINGS --- */}
                    {analysisMode === 'indices' && (
                        <div className="animate-fade-in flex flex-col gap-6">

                            {/* Target Area */}
                            <div className="flex justify-between items-center bg-gray-50 dark:bg-slate-800/50 p-4 rounded-xl border border-gray-200 dark:border-slate-700">
                                <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Target Area</span>
                                <button 
                                    onClick={() => {
                                        setLocationMsg('');
                                        if (!navigator.geolocation) {
                                            setLocationMsg(t.satellite?.locationUnsupported || 'Geolocation is not supported by your browser.');
                                            return;
                                        }
                                        navigator.geolocation.getCurrentPosition(
                                            (p) => {
                                                setParams({ ...params, lat: parseFloat(p.coords.latitude.toFixed(4)), lon: parseFloat(p.coords.longitude.toFixed(4)), polygon_coords: [] });
                                                setLocationMsg('');
                                            },
                                            (err) => {
                                                if (err.code === 1) {
                                                    setLocationMsg(t.satellite?.locationDenied || 'Location access was denied. Please enable it in browser settings.');
                                                } else if (err.code === 2) {
                                                    setLocationMsg(t.satellite?.locationUnavailable || 'Could not determine your location. Try again or select manually.');
                                                } else {
                                                    setLocationMsg(t.satellite?.locationTimeout || 'Location request timed out. Please try again.');
                                                }
                                            },
                                            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
                                        );
                                    }}
                                    className="text-xs flex items-center gap-1 text-[#C47A2C] font-bold hover:text-[#A36322] transition-colors"
                                >
                                    <MapPin size={12} /> {t.satellite?.getLocation || "My Location"}
                                </button>
                            </div>
                            {locationMsg && (
                                <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl text-amber-700 dark:text-amber-400 text-xs font-medium animate-fade-in">
                                    <MapPin size={14} className="shrink-0" />
                                    {locationMsg}
                                </div>
                            )}

                            {/* Coordinates */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex flex-col">
                                    <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-1 ml-1">{t.satellite?.lat || "Latitude"}</label>
                                    <input type="number" step="any" value={params.lat || 0} onChange={(e) => setParams({ ...params, lat: parseFloat(e.target.value) || 0 })} className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-3 text-[#1B263B] dark:text-white font-bold outline-none focus:border-[#C47A2C] shadow-inner transition-colors" />
                                </div>
                                <div className="flex flex-col">
                                    <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-1 ml-1">{t.satellite?.lon || "Longitude"}</label>
                                    <input type="number" step="any" value={params.lon || 0} onChange={(e) => setParams({ ...params, lon: parseFloat(e.target.value) || 0 })} className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-3 text-[#1B263B] dark:text-white font-bold outline-none focus:border-[#C47A2C] shadow-inner transition-colors" />
                                </div>
                            </div>

                            {/* Timeline */}
                            <div className="bg-gray-50 dark:bg-slate-800/50 p-5 rounded-xl border border-gray-200 dark:border-slate-700 flex flex-col gap-5 shadow-sm">
                                <h4 className="text-xs uppercase font-bold text-[#1B263B] dark:text-gray-300 mb-1 tracking-wider border-b border-gray-200 dark:border-slate-700 pb-2">Timeline Settings</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex flex-col">
                                        <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-1 ml-1">{t.satellite?.compareTitle || "Compare Past"}</label>
                                        <input type="number" min="1" value={params.compare_value} onChange={(e) => setParams({ ...params, compare_value: parseInt(e.target.value) })} className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg p-2.5 outline-none focus:border-[#C47A2C] dark:focus:border-[#C47A2C] text-[#1B263B] dark:text-white font-bold tabular-nums" />
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-1 ml-1">{t.satellite?.timeUnit || "Unit"}</label>
                                        <select value={params.compare_unit} onChange={(e) => setParams({ ...params, compare_unit: e.target.value })} className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg p-2.5 outline-none focus:border-[#C47A2C] dark:focus:border-[#C47A2C] text-[#1B263B] dark:text-white font-bold text-sm">
                                            <option value="weeks">{t.satellite?.unitWeeks || "Weeks"}</option>
                                            <option value="months">{t.satellite?.unitMonths || "Months"}</option>
                                            <option value="years">{t.satellite?.unitYears || "Years"}</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex flex-col">
                                        <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-1 ml-1">{t.satellite?.targetDate || "Target Date (Searches past 3 months)"}</label>
                                        <input type="date" value={params.end_date} onChange={(e) => setParams({ ...params, end_date: e.target.value })} className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg p-2.5 outline-none focus:border-[#C47A2C] dark:focus:border-[#C47A2C] text-[#1B263B] dark:text-white font-medium text-sm" />
                                    </div>
                                </div>
                            </div>

                            {/* Scan Radius */}
                            <div className="flex flex-col gap-3">
                                <div className="flex justify-between text-xs font-bold text-[#1B263B] dark:text-gray-300">
                                    <label className="uppercase tracking-widest">{t.satellite?.radius || "Scan Radius"}</label>
                                    <span className="text-[#6B8F71] bg-[#6B8F71]/10 px-2 py-0.5 rounded">{params.radius_km} {t.satellite?.km || "km"}</span>
                                </div>
                                <input type="range" min="1" max="5.0" step="0.1" value={params.radius_km} onChange={(e) => setParams({ ...params, radius_km: parseFloat(e.target.value) })}
                                    disabled={params.polygon_coords.length > 0}
                                    className="w-full accent-[#6B8F71] h-2 bg-gray-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer disabled:opacity-30" />
                            </div>

                            {/* Execute Button */}
                            <button onClick={runAnalysis} disabled={loading} className="w-full mt-4 bg-[#C47A2C] hover:bg-[#A36322] text-white py-4 rounded-xl font-bold shadow-[0_4px_10px_rgba(196,122,44,0.3)] transition-transform transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:hover:translate-y-0 flex justify-center items-center gap-2">
                                {loading ? <Activity size={20} className="animate-spin" /> : <Satellite size={20} />}
                                {loading ? (t.satellite?.loading || "Processing...") : (t.satellite?.run || "Map & Predict Yield")}
                            </button>

                            {/* --- EMBEDDED RESULTS GALLERY --- */}
                            {results && results.indices && !loading && (
                                <div ref={resultsRef} className="flex flex-col gap-6 mt-6 pt-6 border-t border-gray-200 dark:border-slate-700 animate-fade-in">

                                    {/* Area */}
                                    {results.meta?.area_km2 && (
                                        <div className="w-full bg-[#6B8F71]/10 dark:bg-[#6B8F71]/5 p-5 rounded-xl border border-[#6B8F71]/20 flex items-center justify-between shadow-sm">
                                            <div>
                                                <p className="text-[10px] text-[#6B8F71] font-bold uppercase tracking-widest mb-1">Measured Area</p>
                                                <p className="text-3xl font-black text-[#1B263B] dark:text-white drop-shadow-sm">{Number(results.meta.area_km2).toFixed(2)} <span className="text-sm font-medium text-gray-500">km²</span></p>
                                            </div>
                                            <Globe className="text-[#6B8F71]/40" size={48} />
                                        </div>
                                    )}

                                    <div className="space-y-6">
                                        {/* RGB */}
                                        {results.rgb && (
                                            <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-5 border border-gray-200 dark:border-slate-700 shadow-inner flex flex-col">
                                                <h3 className="font-bold text-[#1B263B] dark:text-white mb-4 flex items-center gap-2">
                                                    <Globe size={18} className="text-[#6B8F71]" />
                                                    {t.satellite?.visual || "Visual (RGB)"}
                                                </h3>
                                                {/* التعديل هنا: استخدام grid grid-cols-2 لعرض الصورتين جنب بعض */}
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="flex flex-col gap-1.5">
                                                        <span className="text-[8px] sm:text-[9px] uppercase tracking-tight font-bold text-[#6B8F71] bg-[#6B8F71]/10 self-start px-2 py-0.5 rounded shadow-sm">{t.satellite?.latestScan || "Latest Scan"}</span>
                                                        <SatelliteImage src={results.rgb.current_url} date={results.meta?.date_current} t={t} />
                                                    </div>

                                                    {results.rgb.past_url && (
                                                        <div className="flex flex-col gap-1.5 border-l border-gray-200 dark:border-slate-700 pl-4">
                                                            <span className="text-[8px] sm:text-[9px] uppercase tracking-tight font-bold text-[#C47A2C] bg-[#C47A2C]/10 self-start px-2 py-0.5 rounded shadow-sm">{t.satellite?.historicalScan || "Historical Baseline"}</span>
                                                            <SatelliteImage src={results.rgb.past_url} date={results.meta?.date_past} t={t} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Indices */}
                                        {INDEX_ORDER.map(idxKey => {
                                            const idxData = results.indices[idxKey];
                                            if (!idxData) return null;
                                            return (
                                                <div key={idxKey} className="bg-gray-50 dark:bg-slate-800 rounded-xl p-5 border border-gray-200 dark:border-slate-700 shadow-inner flex flex-col">
                                                    <h3 className="font-bold text-[#1B263B] dark:text-white uppercase mb-4 flex items-center gap-2 tracking-wide">
                                                        <Activity size={18} className="text-[#C47A2C]" />
                                                        {idxKey} {t.satellite[idxKey] ? `- ${t.satellite[idxKey]}` : ''}
                                                    </h3>
                                                    {/* التعديل هنا: استخدام grid grid-cols-2 لعرض الصورتين جنب بعض */}
                                                    <div className="grid grid-cols-2 gap-4">
                                                        {/* CURRENT DATA */}
                                                        <div className="flex flex-col gap-1.5">
                                                            <div className="flex justify-between items-center text-[8px] sm:text-[9px] uppercase tracking-tight font-bold">
                                                                <span className="text-[#6B8F71] bg-[#6B8F71]/10 px-2 py-0.5 rounded shadow-sm">{t.satellite?.latestScan || "Latest Scan"}</span>
                                                                {idxData.current_insight && idxData.current_insight !== 'no_data' && (
                                                                    <span className="text-white bg-[#1B263B] dark:bg-slate-700 px-2 py-0.5 rounded shadow-sm opacity-90">{t.satellite?.[idxData.current_insight] || idxData.current_insight.replace('_', ' ')}</span>
                                                                )}
                                                            </div>
                                                            <SatelliteImage
                                                                src={idxData.current_url}
                                                                date={results.meta?.date_current}
                                                                avg={idxData.current_val ? Number(idxData.current_val).toFixed(2) : '-'}
                                                                t={t}
                                                            />
                                                        </div>

                                                        {/* PAST DATA */}
                                                        {idxData.past_url && (

                                                            <div className="flex flex-col gap-1.5 border-l border-gray-200 dark:border-slate-700 pl-4">
                                                                <div className="flex justify-between items-center text-[8px] sm:text-[9px] uppercase tracking-tight font-bold">
                                                                    <span className="text-[#C47A2C] bg-[#C47A2C]/10 px-2 py-0.5 rounded shadow-sm">{t.satellite?.historicalScan || "Historical Baseline"}</span>
                                                                    {idxData.past_insight && idxData.past_insight !== 'no_data' && (
                                                                        <span className="text-white bg-[#1B263B] dark:bg-slate-700 px-2 py-0.5 rounded shadow-sm opacity-90">{t.satellite?.[idxData.past_insight] || idxData.past_insight.replace('_', ' ')}</span>
                                                                    )}
                                                                </div>
                                                                <SatelliteImage
                                                                    src={idxData.past_url}
                                                                    date={results.meta?.date_past}
                                                                    avg={idxData.past_val ? Number(idxData.past_val).toFixed(2) : '-'}
                                                                    t={t}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* --- PALM COUNT AERIAL MODE SETTINGS --- */}
                    {analysisMode === 'count' && (
                        <div className="space-y-6 animate-fade-in flex flex-col">
                            <div className="border-2 border-dashed border-[#6B8F71]/50 dark:border-[#6B8F71]/30 hover:border-[#6B8F71] bg-[#6B8F71]/5 dark:bg-transparent hover:bg-[#6B8F71]/10 transition-colors rounded-xl p-8 text-center cursor-pointer relative group flex flex-col items-center justify-center min-h-[200px]">
                                <input type="file" multiple onChange={async (e) => {
                                    const files = Array.from(e.target.files);
                                    const newItems = files.map(f => ({ file: f, preview: URL.createObjectURL(f), status: 'pending', count: 0, annotatedImage: null }));
                                    setAerialItems(prev => [...prev, ...newItems]);
                                }} className="absolute inset-0 opacity-0 z-10 cursor-pointer" accept="image/*" />
                                <div className="p-4 bg-white dark:bg-slate-800 rounded-full mb-3 shadow-md group-hover:scale-110 transition-transform">
                                    <Upload size={28} className="text-[#6B8F71]" />
                                </div>
                                <p className="font-bold text-[#1B263B] dark:text-white">{t.satellite?.uploadAerial || "Upload Aerial Photos"}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Dropping images here will queue them for counting.</p>
                            </div>

                            <button onClick={async () => {
                                for (let i = 0; i < aerialItems.length; i++) {
                                    if (aerialItems[i].status !== 'pending') continue;
                                    setAerialItems(prev => prev.map((item, idx) => idx === i ? { ...item, status: 'processing' } : item));

                                    try {
                                        const fd = new FormData();
                                        fd.append("file", aerialItems[i].file);
                                        if (userId) fd.append("user_id", userId);
                                        const json = await ApiService.post('/count-palm-trees', fd, true);
                                        window.dispatchEvent(new CustomEvent('credit-deducted', { detail: { amount: 5 } }));
                                        if (json.status === 'success') {
                                            setAerialItems(prev => prev.map((item, idx) => idx === i ? {
                                                ...item, status: 'success', count: json.palm_count, annotatedImage: json.annotated_image
                                            } : item));
                                        }
                                    } catch (e) {
                                        setAerialItems(prev => prev.map((item, idx) => idx === i ? { ...item, status: 'error' } : item));
                                    }
                                }
                            }} disabled={aerialItems.filter(i => i.status === 'pending').length === 0}
                                className="w-full bg-[#C47A2C] hover:bg-[#A36322] text-white py-4 rounded-xl font-bold shadow-[0_4px_10px_rgba(0,0,0,0.1)] transition-transform transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:hover:translate-y-0">
                                {t.satellite?.run || "Run Object Detection"}
                            </button>

                            {aerialItems.length > 0 && (
                                <button onClick={() => setAerialItems([])} className="w-full text-gray-500 hover:text-red-500 font-medium text-sm py-2 transition-colors">
                                    Clear Gallery
                                </button>
                            )}

                            {/* --- AERIAL RESULTS --- */}
                            {aerialItems.length > 0 && (
                                <div className="flex flex-col gap-5 mt-6 pt-6 border-t border-gray-200 dark:border-slate-700 animate-fade-in">
                                    {aerialItems.map((item, idx) => (
                                        <div key={idx} className="bg-gray-50 dark:bg-slate-800 rounded-xl overflow-hidden shadow-inner border border-gray-200 dark:border-slate-700 flex flex-col">
                                            {/* Status Header */}
                                            <div className="flex justify-between items-center bg-white dark:bg-slate-900 p-4 border-b border-gray-100 dark:border-slate-800">
                                                {item.status === 'success' ? (
                                                    <>
                                                        <span className="text-[#1B263B] dark:text-white font-bold flex items-center gap-2 text-sm">
                                                            <Globe size={16} className="text-[#6B8F71]" />
                                                            {t.satellite?.palmsDetected || "Palms Detected"}
                                                        </span>
                                                        <span className="text-2xl font-black text-[#6B8F71] drop-shadow-sm">{item.count}</span>
                                                    </>
                                                ) : item.status === 'error' ? (
                                                    <span className="text-red-500 font-bold w-full text-center text-sm">Analysis Failed</span>
                                                ) : (
                                                    <span className="text-gray-400 font-bold italic w-full text-center text-sm">Waiting / Scanning</span>
                                                )}
                                            </div>

                                            {/* Stacked images */}
                                            <div className="flex flex-col items-stretch">
                                                <div className="w-full p-3 flex items-center justify-center min-h-[160px] relative bg-[#6B8F71]/5">
                                                    {item.annotatedImage ? (
                                                        <a href={item.annotatedImage} target="_blank" rel="noopener noreferrer" className="w-full cursor-zoom-in">
                                                            <img src={item.annotatedImage} alt="Aerial Output" className="w-full h-[180px] object-cover rounded-lg border border-[#6B8F71]/30 shadow-md hover:opacity-90 transition-opacity" />
                                                        </a>
                                                    ) : item.status === 'error' ? (
                                                        <span className="text-red-400 font-bold">Error Processing</span>
                                                    ) : (
                                                        <div className="flex flex-col items-center opacity-50">
                                                            {item.status === 'processing' ? <Activity size={24} className="text-[#C47A2C] animate-spin mb-2" /> : <Globe size={24} className="mb-2" />}
                                                            <span className="font-bold text-xs tracking-wider uppercase">Processing Image</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="w-full p-3 bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800">
                                                    <p className="text-[10px] text-gray-400 font-bold tracking-widest uppercase mb-2 ml-1">Original Upload</p>
                                                    <a href={item.preview} target="_blank" rel="noopener noreferrer" className="w-full cursor-zoom-in">
                                                        <img src={item.preview} alt="Aerial Input" className="w-full h-[120px] object-cover rounded-lg border border-gray-200 dark:border-slate-700 opacity-60 hover:opacity-100 transition-opacity" />
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                        </div>
                    )}
                </div>
            </div>

            {/* 🟢 اللودينج العائم (Jumping & Glowing Satellite) 🟢 */}
            {loading && (
                <div className="absolute inset-0 bg-[#1B263B]/60 dark:bg-slate-900/80 backdrop-blur-md z-[999] flex flex-col items-center justify-center transition-all">

                    <div className="relative flex flex-col items-center justify-center mb-8">
                        {/* 1. موجات الرادار اللي بتنور وبتوسع (Glowing/Ping effect) */}
                        <div className="absolute w-20 h-20 bg-[#C47A2C]/30 rounded-full animate-ping"></div>
                        <div className="absolute w-16 h-16 bg-[#C47A2C]/50 rounded-full animate-pulse blur-md"></div>

                        {/* 2. أيقونة القمر الصناعي اللي بتنط (Jumping effect) */}
                        <Satellite
                            size={56}
                            className="text-[#C47A2C] animate-bounce drop-shadow-[0_0_20px_rgba(196,122,44,1)] relative z-10"
                        />
                    </div>

                    {/* 3. النص مع تأثير النبض الخفيف */}
                    <p className="text-white font-bold tracking-widest uppercase text-sm drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] animate-pulse">
                        {t.satellite?.loading || "Gathering Satellite Data..."}
                    </p>
                </div>
            )}
        </div>
    );
};
export default SatelliteView;
